const requireContext = require.context('./listingForm', false, /.*\.js$/);
const modulesTemp = requireContext
  .keys()
  .map((file) => [file.replace(/(^.\/)|(\.js$)/g, ''), requireContext(file)])
  .reduce((modules, [name, module]) => {
    if (module.namespaced === undefined) {
      module.namespaced = true;
    }
    return { ...modules, [name]: module };
  }, {});

export const modules = modulesTemp;
import { deleteForUser, getPhotos, upsert } from '@/services/listingDraftRepository';
import { LISTING_DRAFT_SCHEMA_VERSION } from '@/services/listingDraftRecovery';

const DEFAULT_RENT_PERIOD_ID = 2;

function scorePreviewPayload(formData, state) {
  const videos = Array.isArray(formData.videos)
    ? formData.videos.filter(
        (video) => video !== null && video !== undefined && String(video).trim() !== '',
      )
    : [];

  return {
    listing_uuid: state.listingUuid || null,
    property_type_id: formData.property_type_id || null,
    photo_count: state.sect2.media.photoList.length,
    selected_facilities: Array.isArray(formData.selected_facilities)
      ? formData.selected_facilities
      : [],
    videos,
    youtube_url: formData.youtube_url ?? null,
    lat: formData.lat ?? null,
    lng: formData.lng ?? null,
    category_name: formData.category_name ?? null,
    unit_number: formData.unit_number ?? null,
    block_number: formData.block_number ?? null,
    floor: formData.floor ?? null,
    total_floor: formData.total_floor ?? null,
    land_size: formData.land_size ?? null,
    building_size: formData.building_size ?? null,
    bedroom: formData.bedroom ?? null,
    bathroom: formData.bathroom ?? null,
    electrical_power: formData.electrical_power ?? null,
    hoek: formData.hoek ?? null,
    furnishing_status_id: formData.furnishing_status_id ?? null,
  };
}

function valuePresent(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function findById(items, id) {
  if (!valuePresent(id)) return null;
  return items.find((item) => String(item.id) === String(id)) || null;
}

export const state = () => ({
  listingUuid: null,
  status: 'DRAFT',
  isLoading: false,
  fetchingEditListingData: false,
  form: '',
  page: '',
  formCurrentStep: 1,
  listingNumber: null,
  listingType: 'S',
  syncRentfix: true,
  boostStatus: null,
  localDraftId: null,
  localDraftUserUuid: null,
  localDraftRevision: 0,
  localDraftReady: false,
  localDraftHydrated: false,
  listingScorePreview: null,
  listingScorePreviewLoading: false,
  listingScorePreviewError: false,
  listingScorePreviewRequestId: 0,
});

export const getters = {
  allFormData(state, getters) {
    const sect1 = getters['sect1/formData'];
    const sect2 = getters['sect2/formData'];
    const listingUuid = state.listingUuid;
    if (listingUuid !== null) {
      return Object.assign(
        {
          uuid: listingUuid,
          type: state.listingType,
          sync_rentfix: state.listingType === 'R' ? state.syncRentfix : false,
        },
        sect1,
        sect2,
      );
    }
    return Object.assign(
      {
        uuid: null,
        type: state.listingType,
        sync_rentfix: state.listingType === 'R' ? state.syncRentfix : false,
      },
      sect1,
      sect2,
    );
  },
  localDraftSnapshot(state) {
    const location = state.sect1.location;
    const regional = location.regional;
    const map = location.map;
    const detail = state.sect2.detail;
    const media = state.sect2.media;
    return {
      currentStep: state.formCurrentStep,
      listingType: state.listingType,
      syncRentfix: state.syncRentfix,
      propertyTypeId: location.selectedPropertyType && location.selectedPropertyType.id,
      provinceId: regional.province && regional.province.id,
      cityId: regional.city && regional.city.id,
      areaId: regional.area && regional.area.id,
      address: location.address,
      categoryName: location.categoryName,
      unitNumber: location.unitNumber,
      blockNumber: location.blockNumber,
      lat: map.lat,
      lng: map.lng,
      usePinLocation: map.usePinLocation,
      certificateId: detail.selectedCertificate && detail.selectedCertificate.id,
      furnishingStatusId: detail.selectedFurnishingStatus && detail.selectedFurnishingStatus.id,
      rentPeriodId: detail.selectedRentPeriod && detail.selectedRentPeriod.id,
      title: detail.title,
      description: detail.description,
      internalNotes: detail.internalNotes,
      youtubeUrl: detail.youtubeUrl,
      price: detail.price,
      bedroom: detail.bedroom,
      bathroom: detail.bathroom,
      buildingSize: detail.buildingSize,
      landSize: detail.landSize,
      inputSizeUnit: detail.inputSizeUnit,
      totalFloor: detail.totalFloor,
      floor: detail.floor,
      electricalPower: detail.electricalPower,
      hoek: detail.hoek,
      facilities: detail.facilities
        .filter((facility) => facility.selected)
        .map((facility) => ({ id: facility.id, amount: facility.amount })),
      videos: detail.videos.slice(),
      photos: media.photoList.map((photo) => ({
        localId: photo.localId,
        fileName: photo.fileName,
        uuid: photo.uuid || null,
        isPrimary: !!photo.isPrimary,
        uploadStatus: photo.uploadStatus || (photo.uuid ? 'uploaded' : 'pending'),
      })),
    };
  },
  isLocalDraftMeaningful(state, getters) {
    const draft = getters.localDraftSnapshot;
    const values = [
      draft.propertyTypeId,
      draft.provinceId,
      draft.cityId,
      draft.areaId,
      draft.address,
      draft.categoryName,
      draft.unitNumber,
      draft.blockNumber,
      draft.title,
      draft.description,
      draft.internalNotes,
      draft.youtubeUrl,
      draft.price,
      draft.bedroom,
      draft.bathroom,
      draft.buildingSize,
      draft.landSize,
      draft.totalFloor,
      draft.floor,
      draft.electricalPower,
      draft.hoek,
      draft.certificateId,
      draft.furnishingStatusId,
      draft.rentPeriodId,
    ];
    return (
      draft.listingType !== 'S' ||
      draft.usePinLocation ||
      draft.inputSizeUnit !== 'meter' ||
      values.some(valuePresent) ||
      draft.facilities.length > 0 ||
      draft.videos.some(valuePresent) ||
      draft.photos.length > 0
    );
  },
};

export const mutations = {
  SET_FETCHING_EDIT_LISTING_DATA(state, payload) {
    state.fetchingEditListingData = payload;
  },
  SET_FORM_CURRENT_STEP(state, form) {
    state.formCurrentStep = form;
  },
  SET_FORM(state, form) {
    state.form = form;
  },
  SET_PAGE(state, page) {
    state.page = page;
  },
  SET_STATUS(state, page) {
    state.status = page;
  },
  SET_LISTING_TYPE(state, payload) {
    state.listingType = payload;
  },
  SET_SYNC_RENTFIX(state, payload) {
    state.syncRentfix = payload;
  },
  SET_BOOST_STATUS(state, payload) {
    state.boostStatus = payload;
  },
  SET_LISTING_UUID(state, listingUuid) {
    state.listingUuid = listingUuid;
  },
  SET_LISTING_NUMBER(state, listingNumber) {
    state.listingNumber = listingNumber;
  },
  SET_LOADING(state, isLoading) {
    console.log('LOADING: ', isLoading);
    state.isLoading = isLoading;
  },
  SET_LOCAL_DRAFT_SESSION(state, payload) {
    state.localDraftId = payload.draftId;
    state.localDraftUserUuid = payload.userUuid;
    state.localDraftRevision = Number(payload.revision || 0);
  },
  SET_LOCAL_DRAFT_REVISION(state, revision) {
    state.localDraftRevision = revision;
  },
  SET_LOCAL_DRAFT_READY(state, ready) {
    state.localDraftReady = ready;
  },
  SET_LOCAL_DRAFT_HYDRATED(state, hydrated) {
    state.localDraftHydrated = hydrated;
  },
  SET_LISTING_SCORE_PREVIEW(state, payload) {
    state.listingScorePreview = payload;
  },
  SET_LISTING_SCORE_PREVIEW_LOADING(state, loading) {
    state.listingScorePreviewLoading = loading;
  },
  SET_LISTING_SCORE_PREVIEW_ERROR(state, hasError) {
    state.listingScorePreviewError = hasError;
  },
  SET_LISTING_SCORE_PREVIEW_REQUEST_ID(state, requestId) {
    state.listingScorePreviewRequestId = requestId;
  },
};

export const actions = {
  async previewListingScore({ state, getters, commit }) {
    const payload = scorePreviewPayload(getters.allFormData, state);
    const requestId = state.listingScorePreviewRequestId + 1;
    commit('SET_LISTING_SCORE_PREVIEW_REQUEST_ID', requestId);

    if (!payload.property_type_id && !payload.listing_uuid) {
      commit('SET_LISTING_SCORE_PREVIEW', null);
      commit('SET_LISTING_SCORE_PREVIEW_ERROR', false);
      commit('SET_LISTING_SCORE_PREVIEW_LOADING', false);

      return null;
    }

    commit('SET_LISTING_SCORE_PREVIEW_LOADING', true);
    commit('SET_LISTING_SCORE_PREVIEW_ERROR', false);

    try {
      const response = await this.$http.post('/api/v2/listing_scores/preview', payload);
      if (requestId !== state.listingScorePreviewRequestId) return null;

      const preview = response.data.data;
      commit('SET_LISTING_SCORE_PREVIEW', preview);

      return preview;
    } catch (error) {
      if (requestId === state.listingScorePreviewRequestId) {
        commit('SET_LISTING_SCORE_PREVIEW_ERROR', true);
      }

      return null;
    } finally {
      if (requestId === state.listingScorePreviewRequestId) {
        commit('SET_LISTING_SCORE_PREVIEW_LOADING', false);
      }
    }
  },
  setListingType({ commit, state }, listingType) {
    commit('SET_LISTING_TYPE', listingType);

    if (listingType === 'R' && !state.sect2.detail.selectedRentPeriod) {
      const monthlyRentPeriod = state.sect2.detail.rentPeriodList.find(
        (rentPeriod) => Number(rentPeriod.id) === DEFAULT_RENT_PERIOD_ID,
      );

      if (monthlyRentPeriod) {
        commit('sect2/detail/SET_SELECTED_RENT_PERIOD', monthlyRentPeriod);
      }
    }
  },
  async postData({ commit, getters, state }, action) {
    try {
      let formData = getters.allFormData;
      formData.action = action;
      let url = '/api/v2/listing_form/save';
      const {
        data: { data: responseData },
      } = await this.$http.post(url, formData);
      console.log('responseee', responseData);
      //
      commit('SET_LISTING_UUID', responseData.uuid);
      commit('SET_STATUS', responseData.status);
      if (state.form === 'add' && state.localDraftUserUuid) {
        commit('SET_LOCAL_DRAFT_READY', false);
        try {
          await deleteForUser(state.localDraftUserUuid);
        } catch (draftError) {
          console.error('Failed to clear local draft after listing save:', draftError);
        }
      }
      return true;
    } catch (e) {
      throw e;
    }
  },

  async applyBaseData({ dispatch, state, commit }, baseData) {
    commit('SET_LISTING_NUMBER', null);
    if (state.listingUuid != null) {
      commit('SET_BOOST_STATUS', baseData.boost_status);
    }
    return new Promise(async (resolve, reject) => {
      try {
        const form = state.form;
        const applySect1 = dispatch('sect1/applyBaseData', {
          form,
          baseData,
        });
        const applySect2 = dispatch('sect2/applyBaseData', {
          form,
          baseData,
        });

        await Promise.all[(applySect1, applySect2)];
        resolve();
      } catch (e) {
        reject();
      }
    });
  },

  async applyEditData({ commit, dispatch }, editData) {
    await dispatch('sect1/applyEditData', {
      data: editData,
    });
    await dispatch('sect2/applyEditData', {
      data: editData,
    });
    commit('SET_STATUS', editData.status);
    commit('SET_LISTING_NUMBER', editData.listing_number);
    commit('SET_LISTING_TYPE', editData.type);
    if (typeof editData.sync_rentfix === 'boolean') {
      commit('SET_SYNC_RENTFIX', editData.sync_rentfix);
    }
  },

  async fetchBaseData({ commit, dispatch, state }) {
    const form = state.form;
    let responseData;
    if (form === 'add') {
      const {
        data: { data: resultData },
      } = await this.$http.get('/api/v2/listing_form/add');
      responseData = resultData;
      await dispatch('applyBaseData', responseData);
      // commit('location/SET_PROPERTY_TYPE_CATEGORY_GROUP_LIST',responseData.propertyTypeCategoryGroup);
      // commit('location/SET_PROPERTY_TYPE_CATEGORY_LIST',responseData.propertyTypeCategory);
    } else {
      commit('SET_FETCHING_EDIT_LISTING_DATA', true);
      const {
        data: { data: resultData },
      } = await this.$http.get('/api/v2/listing_form/edit/' + state.listingUuid);
      responseData = resultData;
      await dispatch('applyBaseData', responseData.base_data);
      await dispatch('applyEditData', responseData.edit_data);
    }
  },
  restoreInitialState({ commit, dispatch }) {
    commit('SET_FORM_CURRENT_STEP', 1);
    commit('SET_LISTING_UUID', null);
    commit('SET_LISTING_TYPE', 'S');
    commit('SET_SYNC_RENTFIX', true);
    commit('SET_BOOST_STATUS', null);
    commit('SET_STATUS', 'DRAFT');
    commit('SET_LOCAL_DRAFT_SESSION', { draftId: null, userUuid: null, revision: 0 });
    commit('SET_LOCAL_DRAFT_READY', false);
    commit('SET_LOCAL_DRAFT_HYDRATED', false);
    commit('SET_LISTING_SCORE_PREVIEW', null);
    commit('SET_LISTING_SCORE_PREVIEW_LOADING', false);
    commit('SET_LISTING_SCORE_PREVIEW_ERROR', false);
    commit('SET_LISTING_SCORE_PREVIEW_REQUEST_ID', 0);
    dispatch('sect1/restoreInitialState');
    dispatch('sect2/restoreInitialState');
  },
  async initializeLocalDraft({ commit, dispatch }, { draftId, userUuid, draft }) {
    commit('SET_LOCAL_DRAFT_SESSION', {
      draftId,
      userUuid,
      revision: draft ? draft.revision : 0,
    });
    if (draft && draft.payload) await dispatch('hydrateLocalDraft', draft.payload);
    commit('SET_LOCAL_DRAFT_HYDRATED', true);
    commit('SET_LOCAL_DRAFT_READY', true);
  },
  async hydrateLocalDraft({ state, commit, dispatch }, payload) {
    const location = state.sect1.location;
    const regional = location.regional;
    const detail = state.sect2.detail;

    commit('SET_LISTING_TYPE', payload.listingType || 'S');
    commit('SET_SYNC_RENTFIX', payload.syncRentfix !== false);
    commit(
      'sect1/location/SET_SELECTED_PROPERTY_TYPE',
      findById(location.propertyTypeList, payload.propertyTypeId),
    );

    const province = findById(regional.provinces, payload.provinceId);
    await dispatch('sect1/location/regional/changeProvince', province);
    if (province) {
      try {
        await dispatch('sect1/location/regional/getCities');
      } catch (error) {
        console.error('Failed to restore draft cities:', error);
      }
    }
    const city = findById(state.sect1.location.regional.cities, payload.cityId);
    await dispatch('sect1/location/regional/changeCity', city);
    if (city) {
      try {
        await dispatch('sect1/location/regional/getAreas');
      } catch (error) {
        console.error('Failed to restore draft areas:', error);
      }
    }
    await dispatch(
      'sect1/location/regional/changeArea',
      findById(state.sect1.location.regional.areas, payload.areaId),
    );

    commit('sect1/location/SET_ADDRESS', payload.address || null);
    commit('sect1/location/SET_CATEGORY_NAME', payload.categoryName || null);
    commit('sect1/location/SET_UNIT_NUMBER', payload.unitNumber || null);
    commit('sect1/location/SET_BLOCK_NUMBER', payload.blockNumber || null);
    commit('sect1/location/map/SET_LAT', payload.lat ?? null);
    commit('sect1/location/map/SET_LNG', payload.lng ?? null);
    commit('sect1/location/map/SET_USE_PIN_LOCATION', !!payload.usePinLocation);

    commit(
      'sect2/detail/SET_SELECTED_CERTIFICATE',
      findById(detail.certificateList, payload.certificateId),
    );
    commit(
      'sect2/detail/SET_SELECTED_FURNISHING_STATUS',
      findById(detail.furnishingStatusList, payload.furnishingStatusId),
    );
    commit(
      'sect2/detail/SET_SELECTED_RENT_PERIOD',
      findById(detail.rentPeriodList, payload.rentPeriodId),
    );
    const detailValues = {
      SET_TITLE: payload.title,
      SET_DESCRIPTION: payload.description,
      SET_INTERNAL_NOTES: payload.internalNotes,
      SET_YOUTUBE_URL: payload.youtubeUrl,
      SET_PRICE: payload.price,
      SET_BEDROOM: payload.bedroom,
      SET_BATHROOM: payload.bathroom,
      SET_BUILDING_SIZE: payload.buildingSize,
      SET_LAND_SIZE: payload.landSize,
      SET_INPUT_SIZE_UNIT: payload.inputSizeUnit || 'meter',
      SET_TOTAL_FLOOR: payload.totalFloor,
      SET_FLOOR: payload.floor,
      SET_ELECTRICAL_POWER: payload.electricalPower,
      SET_HOEK: payload.hoek,
      SET_VIDEOS: Array.isArray(payload.videos) ? payload.videos : [],
    };
    Object.keys(detailValues).forEach((mutation) => {
      commit(`sect2/detail/${mutation}`, detailValues[mutation] ?? null);
    });

    (payload.facilities || []).forEach((savedFacility) => {
      const facility = detail.facilities.find(
        (item) => String(item.id) === String(savedFacility.id),
      );
      if (!facility) return;
      if (!facility.selected) commit('sect2/detail/TOGGLE_SELECTED_FACILITY', facility.id);
      if (savedFacility.amount !== undefined) {
        commit('sect2/detail/SET_FACILITY_AMOUNT', {
          facilityId: facility.id,
          amount: savedFacility.amount,
        });
      }
    });

    const storedPhotos = await getPhotos(state.localDraftId);
    const blobs = storedPhotos.reduce((result, photo) => {
      result[photo.localId] = photo;
      return result;
    }, {});
    const photos = (payload.photos || []).map((photo) => {
      const stored = blobs[photo.localId];
      return {
        ...photo,
        src: stored ? window.URL.createObjectURL(stored.blob) : '',
        uploadStatus: photo.uuid ? 'uploaded' : photo.uploadStatus || 'pending',
      };
    });
    commit('sect2/media/SET_PHOTO_LIST', photos);
    commit('SET_FORM_CURRENT_STEP', Number(payload.currentStep) === 2 ? 2 : 1);
  },
  async persistLocalDraft({ state, getters, commit }) {
    if (state.form !== 'add' || !state.localDraftReady || !state.localDraftUserUuid) return;
    if (!getters.isLocalDraftMeaningful) {
      await deleteForUser(state.localDraftUserUuid);
      return;
    }
    const revision = state.localDraftRevision + 1;
    commit('SET_LOCAL_DRAFT_REVISION', revision);
    await upsert({
      userUuid: state.localDraftUserUuid,
      draftId: state.localDraftId,
      schemaVersion: LISTING_DRAFT_SCHEMA_VERSION,
      currentStep: state.formCurrentStep,
      payload: getters.localDraftSnapshot,
      revision,
      updatedAt: new Date().toISOString(),
    });
  },
  async discardLocalDraft({ state, commit }) {
    if (state.localDraftUserUuid) await deleteForUser(state.localDraftUserUuid);
    commit('SET_LOCAL_DRAFT_READY', false);
  },
};
